import { useState } from "react";
import { Modal } from "flowbite-react";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import TextareaFormik from "../../renters/components/forms/TextareaFormik";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { maskLicensePlate } from "../../../common/helpers/mask.helpers";
import { IMaintenanceSchedule, MaintenanceScheduleStatus, MaintenanceScheduleType } from "../../../types/maintenanceSchedule.type";
import { createMaintenanceSchedule } from "../../../common/services/maintenance-schedule.api";
import TimeSlotSelector from "./TimeSlotSelector";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";

export type Props = {
  maintenanceSchedule?: IMaintenanceSchedule;
  onClose: () => void;
  onSave: (maintenanceSchedule: IMaintenanceSchedule) => void;
};

const InsuranceClaimSchema = Yup.object().shape({
  licensePlate: Yup.string().required('Placa é obrigatória'),
  status: Yup.string().required('Status é obrigatório'),
  scheduledDate: Yup.date().required('Data é obrigatória'),
  scheduledTime: Yup.string().required('Hora é obrigatória'),
});


export function ModalCreateMaintenanceSchedules(props: Props) {
  const [, setIsLoading] = useState(false);
  const { notifyError } = useToastContext();

  const initialValues = props.maintenanceSchedule || {
    licensePlate: '',
    companyId: '',
    typeReview: '',
    author: '',
    renterName: '',
    renterPhone: '',
    renterVehicle: '',
    scheduledDate: '',
    scheduledTime: '',
    status: MaintenanceScheduleStatus.Scheduled,
    notes: '',
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const timeMatch = values.scheduledTime.match(/(\d{2}:\d{2})/);
      const formattedTime = timeMatch ? timeMatch[0] : values.scheduledTime;

      if (values.typeReview === "")
        return notifyError("Tipo de agendamento é obrigatório");

      const [year, month, day] = values.scheduledDate.split('-');
      const [hours, minutes] = formattedTime.split(':');

      const localDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        0,
        0
      );
      const formattedValues: IMaintenanceSchedule = {
        ...values,
        scheduledDate: localDate,
        scheduledTime: formattedTime
      };

      const { ...restWithoutId } = formattedValues;

      const result = await createMaintenanceSchedule(restWithoutId)

      if (!result.ok)
        return notifyError(result.message || "Erro ao criar agendamento de manutenção");

      props.onSave(result.data);

      props.onClose();
    } catch (error) {
      console.error("Erro ao salvar o agendamento:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      className='relative text-black'
      show={true}
      onClose={() => props.onClose()}
      size="7x"
      theme={{
        content: {
          base: "relative h-full w-full p-4 md:h-auto",
          inner: "relative rounded-lg bg-white shadow dark:bg-gray-700 max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col"
        }
      }}
    >
      <Modal.Header className='text-black'>
        <h2 className='text-black'>
          {initialValues.licensePlate ?
            `Agendamento: ${initialValues.renterName ?
              initialValues.renterName.split(' ').map(name =>
                name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
              ).join(' ') : 'Desconhecido'
            }` : "Novo Agendamento de Manutenção"}
        </h2>
      </Modal.Header>
      <Modal.Body className="min-h-[509px]">
        <Formik
          initialValues={initialValues}
          validationSchema={InsuranceClaimSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="space-y-4 border rounded-lg p-3">

                <div className="resumo">
                  <div className="flex flex-col md:flex-row flex-wrap">
                    <div className="flex flex-col md:flex-row md:flex-wrap">
                      <div className="w-full md:w-1/4 md:pr-2 mb-4">
                        <ComboBoxFormik
                          label="Tipo de agendamento:"
                          name="typeReview"
                          options={[
                            { value: "", label: "-" },
                            { value: MaintenanceScheduleType.Preventive, label: "Revisão Preventiva" },
                            { value: MaintenanceScheduleType.Corrective, label: "Revisão Corretiva" },
                            { value: MaintenanceScheduleType.Other, label: "Outros" },
                          ]}
                        />
                      </div>

                      <div className="w-full md:w-1/4 md:pr-2 mb-4">
                        <InputFormik
                          label="Data:"
                          name="scheduledDate"
                          type="date"
                          className="w-full"
                          required={true}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className="w-full md:w-1/4 md:px-2 mb-4">
                        <TimeSlotSelector />
                      </div>
                      <div className="w-full md:w-1/4 md:pl-2 mb-4">
                        <InputFormik
                          label="Placa:"
                          name="licensePlate"
                          className="w-full"
                          required={true}
                          mask={maskLicensePlate}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap mb-4 mt-1">
                    <div className="w-full">
                      <label htmlFor="notes" className="block mb-2 text-sm font-medium">
                        Observações:
                      </label>
                      <TextareaFormik
                        label=""
                        name="notes"
                        className="w-full min-h-[150px] rounded shadow-md focus:border-white p-2 border border-gray-300"
                        placeholder="Descreva informações relevantes..."
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">

                  <ButtonWithLoader
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Salvar
                  </ButtonWithLoader>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}